import { useSyncExternalStore } from "react";

type ApplyUpdate = (reloadPage?: boolean) => Promise<void>;

let pendingUpdate: ApplyUpdate | null = null;
const listeners = new Set<() => void>();

const setPendingUpdate = (update: ApplyUpdate | null) => {
  pendingUpdate = update;
  listeners.forEach((listener) => listener());
};

/**
 * Called by the service worker registration (main.tsx) when a new version is
 * waiting. Kept out of the components so they never import the
 * `virtual:pwa-register` module, which only exists in the Vite app build.
 */
export const notifyUpdateAvailable = (applyUpdate: ApplyUpdate) =>
  setPendingUpdate(applyUpdate);

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useServiceWorkerUpdate = () => {
  const update = useSyncExternalStore(
    subscribe,
    () => pendingUpdate,
    () => null,
  );
  return {
    isUpdateAvailable: update !== null,
    /** Activates the waiting service worker, then reloads the page. */
    applyUpdate: () => update?.(true),
    dismissUpdate: () => setPendingUpdate(null),
  };
};
